import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { StrategicRecommendation, StrategicRecommendationFeature } from 'types'
import { useModals } from 'almanac-ui/Modal'
import Styles from './DetailsModal.module.scss'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Row } from './Row'
import { useState } from 'react'
import {
  updateOptimistically,
  updateRecommendation,
} from 'hooks/queries/brainStrategicRecommendations/useUpdateRecommendation'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import {
  deleteOptimistically,
  deleteRecommendation,
} from 'hooks/queries/brainStrategicRecommendations/useDeleteRecommendation'
import { useQueryClient } from 'react-query'
import { reportError } from 'utils/error'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { useCreateStrategicRecommendations } from 'hooks/queries/brandKits/useCreateStrategicRecommendations'
import { useBatch } from 'hooks/queries/batches/useBatch'
import { fetchStrategicRecommendations } from 'hooks/queries/brandKits/useBrandKitStrategicRecommendations'
import groupBy from 'lodash/groupBy'
import { Heading } from 'blaze-ui/Heading/Heading'
import { CHANNEL_LABELS, FEATURE_LABELS } from '../constants'
import { FeatureIcon } from '../FeatureIcon'

interface Props {
  channel_type: StrategicRecommendation['channel_type']
  brandKitId: number
  recommendations: StrategicRecommendation[]
}

const DetailsModal = ({ channel_type, brandKitId, recommendations }: Props) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const canDebug = useFlipperFlag('brain_strategic_recommendations_debug')
  const { closeModal } = useModals()

  const [stagedRecommendations, setStagedRecommendations] = useState<StrategicRecommendation[]>(recommendations)
  const [isSaving, setIsSaving] = useState(false)
  const [batchId, setBatchId] = useState<string>()

  const { mutateAsync: createStrategicRecommendations, isLoading: isCreating } = useCreateStrategicRecommendations()
  useBatch(batchId, {
    pollUntilFinal: true,
    onComplete: async () => {
      setBatchId(undefined)
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'strategicRecommendations'])
      const newRecommendations = await fetchStrategicRecommendations({ brandKitId, workspaceId })
      setStagedRecommendations(newRecommendations)
    },
  })

  const isRegenerating = isCreating || !!batchId

  const handleChange = (id: number, recommendationText: string) => {
    setStagedRecommendations((prev) => {
      return prev.map((recommendation) => {
        if (recommendation.id === id) {
          return { ...recommendation, recommendation: recommendationText }
        }
        return recommendation
      })
    })
  }

  const handleDelete = (id: number) => {
    setStagedRecommendations((prev) => {
      return prev.filter((recommendation) => recommendation.id !== id)
    })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const updatedRecommendations = stagedRecommendations.filter((recommendation) => {
        const originalRecommendation = recommendations.find((r) => r.id === recommendation.id)
        if (!originalRecommendation) {
          return false
        }
        return recommendation.recommendation !== originalRecommendation.recommendation
      })

      const deletedRecommendations = recommendations.filter((recommendation) => {
        return !stagedRecommendations.some((r) => r.id === recommendation.id)
      })

      for (const recommendation of updatedRecommendations) {
        const variables = {
          workspaceId,
          brandKitId,
          recommendationId: recommendation.id,
          recommendation: recommendation.recommendation,
        }
        await updateOptimistically({ queryClient, ...variables })
        await updateRecommendation(variables)
      }

      for (const recommendation of deletedRecommendations) {
        const variables = {
          workspaceId,
          brandKitId,
          recommendationId: recommendation.id,
        }
        await deleteOptimistically({ queryClient, ...variables })
        await deleteRecommendation(variables)
      }

      setIsSaving(false)
      closeModal()
    } catch (error) {
      reportError(error)
      setIsSaving(false)
    }
  }

  const handleRegenerateAll = async () => {
    const { bid } = await createStrategicRecommendations({ brandKitId, channel_type })
    setBatchId(bid)
  }

  const groupedByFeature = groupBy(stagedRecommendations, 'feature')

  return (
    <Modal.Root size="lg" data-testid="strategic-recommendations-details-modal">
      <Modal.Header
        title={CHANNEL_LABELS[channel_type]}
        actions={
          <Button
            size="md"
            variant="ghost"
            frontIcon={ArrowRefresh2}
            onPress={handleRegenerateAll}
            isDisabled={isRegenerating}
            aria-label={isRegenerating ? 'Regenerating...' : 'Regenerate All'}
          >
            {isRegenerating ? 'Regenerating...' : 'Regenerate All'}
          </Button>
        }
      />

      <Modal.Content>
        <div className={Styles.tables}>
          {Object.entries(groupedByFeature).map(([feature, recommendations]) => (
            <table className={Styles.table} key={feature}>
              <thead>
                <tr>
                  <th>
                    <div className={Styles.featureHeaderContent}>
                      <FeatureIcon feature={feature as StrategicRecommendationFeature} size={20} />
                      <Heading level={4} color="var(--dark-80)">
                        {FEATURE_LABELS[feature as StrategicRecommendationFeature]} Recommendations
                      </Heading>
                    </div>
                  </th>
                  <th>
                    <Heading level={4} color="var(--dark-80)">
                      Reasoning
                    </Heading>
                  </th>
                  {canDebug && (
                    <th>
                      <Heading level={4} color="var(--dark-80)">
                        AI Output (Internal only)
                      </Heading>
                    </th>
                  )}
                  <th style={{ width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {recommendations.map((recommendation) => (
                  <Row
                    key={recommendation.id}
                    recommendation={recommendation}
                    onChange={handleChange}
                    onDelete={handleDelete}
                    isDisabled={isRegenerating}
                  />
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={closeModal}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" variant="primary" onPress={handleSave} isDisabled={isSaving}>
            Save
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export { DetailsModal }
